<template>
  <div>
    <serveur-info :erreur="erreur" />
    <my-notification :icon="'cogs'">
      <template #title>
        {{ $t('compTransitionActionsMain.title') }}
      </template>
      <p>
        <span v-html="$t('compTransitionActionsMain.info')" />
      </p>
    </my-notification>
    <br />

    <!-- Creation/Update form below -->
    <b-modal
      v-if="currentAction"
      v-model="displayModal"
      active
      has-modal-card
      :can-cancel="['escape', 'x', 'outside']"
    >
      <action-editor
        :disabled="false"
        :action="currentAction"
        :erreur="erreur"
        :quota="quota"
        :maxQuota="maxQuota"
        :actionQuota="actionQuota"
        @onActionEditorCancel="actionEditorCancel"
        @onActionEditorSave="actionEditorSave"
        @onDeleteActionFile="actionFileDelete"
      >
      </action-editor>
    </b-modal>
    <!-- /Creation/Update form -->
    <actions-table
      :actions="actions"
      :searchable="false"
      :withCreatedAtColumn="false"
      withValidColumn
    >
      <!-- custom stuff on the last column-->
      <template #custom="slotProps">
        <b-tooltip
          :label="$t('compTransitionActionsMain.edit')"
          type="is-black"
        >
          <b-button
            size="is-small"
            icon-left="pencil"
            @click="openAction(slotProps.action)"
          />
        </b-tooltip>
        <b-tooltip
          :label="$t('compTransitionActionsMain.view')"
          type="is-black"
        >
          <b-button
            size="is-small"
            icon-left="eye"
            @click="viewAction(slotProps.action)"
          />
        </b-tooltip>
        <b-tooltip
          :label="$t('compTransitionActionsMain.delete')"
          type="is-black"
        >
          <b-button
            size="is-small"
            icon-left="trash"
            @click="confirmDelete(slotProps.action)"
          />
        </b-tooltip>
        <b-tooltip
          :label="$t('compTransitionActionsMain.submit')"
          type="is-black"
        >
          <b-button
            :disabled="
              slotProps.action.adminStatus.descriptor != 'adminstatus:draft'
            "
            size="is-small"
            icon-left="paper-plane"
            @click="confirmSubmit(slotProps.action)"
          />
        </b-tooltip>
      </template>
    </actions-table>
    <br />
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <div class="level-item">
          <b-button icon-left="plus" @click="displayAddForm()">
            {{ $t('add') }}
          </b-button>
        </div>
        <div class="level-item" v-if="scenarios.length > 0">
          <b-select
            placeholder="Depuis un scenario"
            icon="plus"
            @input="importFromScenario"
          >
            <option
              :value="scenario"
              v-for="(scenario, index) in scenarios"
              :key="`scenario-${index}`"
            >
              {{ scenario.name }}
            </option>
          </b-select>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { Background } from '@/mixins/background.js'
import transitionService from '@/services/transitionService'
import coreService from '@/services/coreService'

import { useAdminStore } from '@/stores/admin.js'
import Scenario from '@/models/scenario/Scenario.js'

import { entityFactory } from '@/models/core'

import {
  PublicAction,
  TAGS,
  insertOrUpdateInList,
  removeInActions,
  moduleToTag
} from '@/models/transition/transition.js'
import { USER_STATUSES } from '@/models/transition/transition'

export default {
  name: 'PublicActionsEditor',
  props: {},
  components: {
    'serveur-info': () => import('@/components/ServeurInfo.vue'),
    'my-notification': () => import('@/components/MyNotification.vue'),
    'actions-table': () => import('@/components/transition/ActionsTable.vue'),
    'action-editor': () => import('@/components/transition/actions/Edit.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  mixins: [Background],
  emit: [
    'onValidAction',
    'onViewAction',
    'onActionSavedSuccess',
    'onActionSavedDeleted',
    'onActionDeletedSuccess',
    'onActionDeletedDeleted',
    'onActionFileDeletedSucess'
  ],
  data: function () {
    return {
      quota: 0,
      maxQuota: 0,
      actionQuota: 0,
      // Erreur catch somehow and displayed by the serveur-info component
      erreur: '',
      // The list of possible tags
      // It's used to make suggestions on the corresponding tag inputs
      availableTags: TAGS,
      // The list of Statused
      // It's used to make suggestions on the corresponding tag inputs
      statuses: USER_STATUSES,
      // entity
      entity: null,
      // the list of public actions
      actions: [],
      // hold the currentAction for creation/update
      currentAction: new PublicAction(),
      // whether we have to display the creation/update modal
      displayModal: false,
      // Used for vue2-editor in the creation/edition modal
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['blockquote', 'code-block'],
        // remove image as we're not ready store them in db
        ['link'],
        ['clean']
      ],
      scenarios: []
    }
  },
  methods: {
    actionEditorCancel (action) {
      this.closeModal()
    },
    confirmDelete (action) {
      this.$buefy.dialog.confirm({
        title: this.$t('compTransitionActionsMain.delete-title'),
        message: this.$t('compTransitionActionsMain.confirm-delete'),
        confirmText: this.$t('validate'),
        cancelText: this.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: () => this.deleteAction(action)
      })
    },
    confirmSubmit (action) {
      this.$buefy.dialog.confirm({
        title: this.$t('compTransitionActionsMain.submit-title'),
        message: this.$t('compTransitionActionsMain.ask-submit'),
        confirmText: this.$t('validate'),
        cancelText: this.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: () => this.submitAction(action)
      })
    },
    closeModal () {
      this.displayModal = false
    },
    displayAddForm (p) {
      // create a new action (no id yet)
      this.currentAction = new PublicAction().setEntity(this.entity)
      this.displayModal = true
    },
    _openAction (action) {
      // internal method
      this.currentAction = action.clone()
      this.displayModal = true
    },
    /**
     * Open an action for editing
     * @param {*} action
     */
    openAction (action) {
      // working on a copy to easily rollback if needed
      if (!action.isDraft()) {
        this.$buefy.dialog.confirm({
          title: this.$t('compTransitionActionsMain.warning-no-draft-title'),
          message: this.$t('compTransitionActionsMain.warning-no-draft-info'),
          confirmText: this.$t('validate'),
          cancelText: this.$t('cancel'),
          canCancel: ['escape', 'button', 'outside'],
          onConfirm: () => {
            this._openAction(action)
          }
        })
      } else {
        this._openAction(action)
      }
    },
    /**
     * View the action
     * The behaviour here depends on the status
     * draft / published => view the action (as it is published)
     * submitted => go to the reviewer room
     * @param {*} action
     */
    viewAction (action) {
      // using the public endpoint which should be ok because we own the action (it's likely not published yet!)
      let route = this.$router.resolve({
        path: `/transition-1point5/reviewer/actions/${action.id}`
      })
      window.open(route.href, '_blank')
    },
    onSaveSuccess () {
      this.toast(true, this.$t('compTransitionActionsMain.action-saved'))()
    },
    onSaveError () {
      this.toast(false, this.$t('compTransitionActionsMain.action-saved'))()
      this.$emit('onActionSavedError')
    },
    /**
     * Submit an action
     */
    submitAction (action) {
      this.bgUI(
        async () => {
          let data = await transitionService.submitPublicAction(action.id)
          if (data) {
            let newAction = PublicAction.fromDatabase(data)
            insertOrUpdateInList(this.actions, newAction)
          }
          // what if we don't get data here
        },
        this.onSubmitSuccess,
        this.onSubmitError
      )
    },
    /**
     * Save an individual action
     * close the modal once it's saved
     */
    actionEditorSave (event) {
      let action = event.action
      let files = event.files
      this.validateBgUi(
        async () => {
          let data = await transitionService.savePublicAction(
            action.toDatabase(),
            files
          )
          if (data) {
            let newAction = PublicAction.fromDatabase(data)
            // TODO(msimonin insert into the list)
            insertOrUpdateInList(this.actions, newAction)
            this.updateQuota()
            this.closeModal()
          } else {
            // force a Error to execute onSaveError callback
            // We ask to save a Plan but we got nothing in return
            throw Error(
              'Something went wrong when loading back the transition Lab'
            )
          }
        },
        this.onSaveSuccess,
        this.onSaveError
      )
    },
    onDeleteSuccess () {
      this.toast(true, this.$t('compTransitionActionsMain.action-deleted'))()
    },
    onDeleteError () {
      this.toast(false, this.$t('compTransitionActionsMain.action-deleted'))()
      // fire some event to indicates that we have a valid and saved transition lab
      this.$emit('onActionDeletedError')
    },

    /**
     * Delete an action
     */
    deleteAction (action) {
      this.bgUI(
        async () => {
          await transitionService.deletePublicAction(action.id)
          // what if we don't get data here
          removeInActions(this.actions, action)
          this.updateQuota()
        },
        this.onDeleteSuccess,
        this.onDeleteError
      )
    },
    onSubmitSuccess () {
      this.toast(true, this.$t('compTransitionActionsMain.action-submitted'))()
    },
    onSubmitError () {
      this.toast(false, this.$t('compTransitionActionsMain.action-submitted'))()
      this.$emit('onActionSubmittedError')
    },
    actionFileDelete (file) {
      this.bgUI(async () => {
        await transitionService.deletePublicActionFile(file)
        // make it consistent with the currentAction copy of the original one
        if (this.currentAction) {
          this.currentAction.deleteFile(file)
          this.actions
            .filter((a) => a.id === this.currentAction.id)[0]
            .deleteFile(file)
          this.updateQuota()
        }
        this.$emit('onActionFileDeletedSuccess', file)
      })
    },
    importFromScenario (scenario) {
      this.$buefy.dialog.confirm({
        title: this.$t('compTransitionActionsMain.import-actions'),
        message:
          this.$t('compTransitionActionsMain.confirm-import-actions') +
          `(${scenario.rules.length} actions)`,
        confirmText: this.$t('validate'),
        cancelText: this.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: () => this._importFromScenario(scenario)
      })
    },
    _importFromScenario (scenario) {
      scenario.rules.forEach((r) => {
        try {
          let action = new PublicAction().setTitle(r.humanTitle('fr'))
          let tags = moduleToTag(r.module)
          if (tags) {
            action.setTags(tags)
          }

          let route = this.$router.resolve({
            path: '/scenario-1point5-results/' + scenario.uuid
          })
          action.setText(`<a href="${route.href}">Reference</a>`)

          transitionService
            .savePublicAction(action.toDatabase(), [])
            .then((data) => {
              let newAction = PublicAction.fromDatabase(data)
              insertOrUpdateInList(this.actions, newAction)
            })
        } catch (error) {
          // don't fail too hard if a rule can't be exported
        }
      })
    },
    /**
     * Update the global quota. This is used everytime files are touched (added, deleted).
     * This ensure a strong consistency between the displayed quota and the backend.
     */
    updateQuota () {
      transitionService.getQuota().then((quota) => {
        this.maxQuota = quota.quota_global
        this.actionQuota = quota.quota_action
        // this.quota = (100 * quota.current) / quota.total
        this.quota = quota.current
      })
    }
  },
  setup () {
    const adminStore = useAdminStore()
    return { adminStore }
  },
  mounted () {
    this.bgUI(
      async () => {
        let [actions, entity] = await Promise.all([
          transitionService.getPublicActions(),
          coreService.getEntity() // crap !
        ])
        this.actions = actions.map((a) => PublicAction.fromDatabase(a))
        const EntityClass = entityFactory(entity.type)
        this.entity = EntityClass.createFromObj(entity.data)

        this.updateQuota()
        try {
          let scenarios = await this.adminStore.getScenarios()
          this.scenarios = scenarios.map((s) => Scenario.createFromObj(s))
        } catch (error) {
          // purpose be on the link with scenario
          this.scenarios = []
        }
      },
      this.toast(true, this.$t('compTransitionActionsMain.actions-loaded')),
      this.toast(false, this.$t('compTransitionActionsMain.actions-loaded'))
    )
  }
}
</script>

<style>
.select.is-empty select {
  color: black !important;
}
</style>
